import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Alert, Avatar, Button, Checkbox, Col, Form, Input, notification, Radio, Row, Select, Space, Typography, Upload} from 'antd';
import {CameraOutlined, EditOutlined, SearchOutlined, ShoppingCartOutlined, UserOutlined} from '@ant-design/icons';
import {Image, HighlighterCircle, ArrowFatLineUp, EnvelopeSimple, UserFocus} from '@phosphor-icons/react';
import {IJobDemandReq, IJobDetails, ILocation, JobTypeOption} from "../../services/types.ts";
import {
    addJobDemand,
    createJobCheckoutSession,
    getJobById,
    getJobPricing,
    getJobTypes,
    updateJobDemand,
    uploadClientPhoto
} from "../../services/api.ts";
import {ENGAGEMENT_FORM_OPTIONS, WORK_LOAD_HOURS_OPTIONS} from "../../util/jobFormOptions.ts";
import {useQuery, UseQueryResult} from "@tanstack/react-query";
import {AuthContext} from "../../context/AuthContext.tsx";
import {AddressModal} from "../../components/AddressModal.tsx";
import {useNavigate, useSearchParams} from "react-router-dom";
import {PageTitle} from "candidate/src/components/PageTitle.tsx";
import {useMediaQuery} from "@uidotdev/usehooks";
import styled from "styled-components";
import {StyledInput} from "candidate/src/pages/jobPool/JobPool.tsx";
import {FAQ} from "./FAQ.tsx";
import {Loader} from "../../components/Loader.tsx";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import {colors} from "../../util/colors.ts";

const {Title, Text} = Typography;

const ADDON_DISPLAY = [
    {key: 'logo', icon: <Image size={18} weight="bold"/>, label: 'Unternehmens Logo', price: 9, description: 'Bis zu 2x mehr Sichtbarkeit! Ihr Logo wird neben der Anzeige gezeigt.'},
    {key: 'highlight', icon: <HighlighterCircle size={18} weight="bold"/>, label: 'Highlight', price: 19, description: 'Bis zu 2x mehr Sichtbarkeit! Ihre Anzeige wird farbig hervorgehoben.'},
    {key: 'top_position', icon: <ArrowFatLineUp size={18} weight="bold"/>, label: 'Top-Anzeige', price: 29, description: 'Bis zu 3x mehr Sichtbarkeit! Deine Anzeige steht am Anfang der Liste.'},
    {key: 'email_distribution', icon: <EnvelopeSimple size={18} weight="bold"/>, label: 'E-Mail Verteilung', price: 79, description: 'Bis zu 10x mehr Sichtbarkeit! Ihre Anzeige wird alle 30 Tage passenden Kandidaten per E-Mail zugesandt.'},
    {key: 'direct_contact', icon: <UserFocus size={18} weight="bold"/>, label: 'Direktansprache', price: 99, description: 'Unbegrenzt über 60.000 Kandidaten direkt kontaktieren.'},
];

const initialValues = {
    description: `
        <p><strong>[Tipp: Beschreiben Sie kurz und prägnant worum es geht]</strong></p>
        <p><em>Beispiel:</em> Wir suchen einen neuen Kollegen für unser XYZ Team, der uns bei der Bearbeitung von telefonischen Kundenanfragen und Ablage unterstützt.</p>
        <br/>
        <p><strong>Wo?</strong></p>
        <p><strong>[Tipp: Nennen Sie die konkrete Anschrift und ggf. auch öffentl. Verkehrsanbindung, wenn es mehrere Standorte gibt, nennen Sie alle]</strong></p>
        <ul>
            <li><em>Beispiel:</em> Grolmanstraße 48, 10623 Berlin, nähe S-Bahn Savignyplatz</li>
            <li><em>Beispiel:</em> An folgenden Standorten suchen wir: Charlottenburg, Wedding und Köpenick</li>
        </ul>
        <br/>
        <p><strong>Was wir bieten?</strong></p>
        <p><strong>[Tipp: Verkaufen Sie sich gut, Silvertalents sind stark nachgefragt]</strong></p>
        <ul>
            <li><em>Beispiel:</em> Obstkorb und Kaffee im Büro für alle</li>
            <li><em>Beispiel:</em> nettes Arbeitsumfeld</li>
        </ul>
        <br/>
        <p><strong>Was wir suchen?</strong></p>
        <p><strong>[Tipp: Wenn es wichtige Anforderungen gibt listen Sie sie auf, aber denken Sie daran, dass jede Anforderung den Bewerberkreis verkleinert]</strong></p>
        <ul>
            <li><em>Beispiel:</em> motivierte neue Kollegen</li>
            <li><em>Beispiel:</em> Kenntnisse in XYZ</li>
        </ul>
    `
};

const DRAFT_ADDONS_KEY = (id: number) => `draft_addons_${id}`;

const jobDetailsToLocation = (job: IJobDetails): ILocation => ({
    id: job.search_location_id,
    label: job.search_location_label || '',
    label_client: job.search_location_label || '',
    label_candidate: job.search_location_candidate_label || job.search_location_label || '',
    type: job.regions_id && job.regions_id > 0 ? 'region_id' : 'postal_code',
    value: (job.regions_id && job.regions_id > 0 ? job.regions_id : job.postal_code) as number | undefined,
    radius: job.radius ?? 0,
    created_at: 0,
    pattern: '',
    search: '',
    default: '',
    show: 1,
    show_client: 1,
});

export const NewJobDemandPricing = () => {
    const [searchParams] = useSearchParams();
    const draftId = searchParams.get('draftId');
    const [courseDetails, setCourseDetails] = useState<string>(initialValues.description);
    const {user} = useContext(AuthContext);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isWhereModalOpen, setIsWhereModalOpen] = useState(false);
    const [whereSelectedValue, setWhereSelectedValue] = useState<ILocation | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");

    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

    const basePrice = selectedCategory === 'fachkraefte' ? 299 : 0;
    const addonsTotal = ADDON_DISPLAY
        .filter(a => selectedAddons.includes(a.key))
        .reduce((sum, a) => sum + a.price, 0);
    const totalMonthly = basePrice + addonsTotal;
    const [logoUrl, setLogoUrl] = useState<string | null>(user?._client?.profile_photo?.url || null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        if (!user?._client?.profile_name && !user?._client?.isPrivate) {
            navigate("/new/profile");
        }
    }, [navigate, user?._client]);

    const {data: jobTypes}: UseQueryResult<JobTypeOption[], Error> = useQuery({
        queryKey: ['jobTypes'],
        queryFn: async () => {
            const types = await getJobTypes();
            return types.map(type => ({...type, value: type.id}));
        }
    });

    useQuery({
        queryKey: ['jobPricing'],
        queryFn: getJobPricing
    });

    const {data: draftJob} = useQuery({
        queryKey: ['jobs', draftId],
        queryFn: () => getJobById(draftId!),
        enabled: !!draftId,
    });

    // Prefill form and state when draft loads - use initialValues so Form gets correct values on mount
    const formInitialValues = useMemo(() => {
        if (!draftJob) return initialValues;
        return {
            ...initialValues,
            title: draftJob.title,
            location: draftJob.search_location_label,
            description: draftJob.description,
            job_type: draftJob.job_type_id,
            engagement_form: draftJob.engagement_form ?? undefined,
            work_load_min_hours: draftJob.work_load_min_hours ?? undefined,
            work_load_max_hours: draftJob.work_load_max_hours ?? undefined,
            contactEmail: draftJob.contactEmail ?? undefined,
        };
    }, [draftJob]);

    useEffect(() => {
        if (!draftJob) return;
        setCourseDetails(draftJob.description || initialValues.description);
        setWhereSelectedValue(jobDetailsToLocation(draftJob));
        setSelectedCategory(draftJob.job_category || null);
        // Prefill addons: from API (has_logo etc.) or from sessionStorage (saved when creating draft)
        const addonsFromApi: string[] = [];
        if (draftJob.has_logo) addonsFromApi.push('logo');
        if (draftJob.has_highlight) addonsFromApi.push('highlight');
        if (draftJob.has_top_position) addonsFromApi.push('top_position');
        if (draftJob.has_email_distribution) addonsFromApi.push('email_distribution');
        if (draftJob.has_direct_contact) addonsFromApi.push('direct_contact');
        const storedAddons = draftJob.id && (() => {
            try {
                const raw = sessionStorage.getItem(DRAFT_ADDONS_KEY(draftJob.id!));
                return raw ? (JSON.parse(raw) as string[]) : null;
            } catch {
                return null;
            }
        })();
        setSelectedAddons(addonsFromApi.length > 0 ? addonsFromApi : (storedAddons || []));
        form.setFieldsValue({
            title: draftJob.title,
            location: draftJob.search_location_label,
            description: draftJob.description,
            job_type: draftJob.job_type_id,
            engagement_form: draftJob.engagement_form ?? undefined,
            work_load_min_hours: draftJob.work_load_min_hours ?? undefined,
            work_load_max_hours: draftJob.work_load_max_hours ?? undefined,
            contactEmail: draftJob.contactEmail ?? undefined,
        });
    }, [draftJob, form]);

    const handleSaveWhere = useCallback((value: ILocation | null) => {
        if (value) {
            form.setFieldsValue({location: value.label_client});
            setWhereSelectedValue(value);
        }
    }, [form]);

    const handleAddonToggle = (addonKey: string) => {
        setSelectedAddons(prev =>
            prev.includes(addonKey)
                ? prev.filter(k => k !== addonKey)
                : [...prev, addonKey]
        );
    };

    const handleLogoUpload = async (file: File) => {
        if (!user?.client_id) return;
        setUploadingLogo(true);
        try {
            const result = await uploadClientPhoto(user.client_id, file);
            setLogoUrl(result.profile_photo?.url || null);
            notification.success({message: 'Firmenlogo erfolgreich hochgeladen'});
        } catch (e) {
            console.error(e);
            notification.error({message: 'Fehler beim Hochladen des Logos'});
        } finally {
            setUploadingLogo(false);
        }
    };

    const buildJobDemand = (): IJobDemandReq | null => {
        const formValues = form.getFieldsValue();
        if (!user || !whereSelectedValue || !selectedCategory) return null;

        return {
            type: "job_ad",
            title: formValues.title,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            description: formValues.description,
            job_type_id: formValues.job_type || 0,
            work_load_id: 0,
            job_type_label: jobTypes?.find(type => type.id === formValues.job_type)?.label || '',
            job_demand_status_id: 3,
            client_id: user.client_id,
            users_id: user.id,
            search_location_label: whereSelectedValue.label,
            search_location_id: whereSelectedValue.id,
            regions_id: whereSelectedValue.type === 'region_id' ? whereSelectedValue.value || 0 : 0,
            postal_code: whereSelectedValue.type !== 'region_id' ? whereSelectedValue.value?.toString() || '' : '',
            radius: whereSelectedValue.radius || 0,
            contactEmail: formValues.contactEmail || null,
            pricing_model: 'per_job',
            job_category: selectedCategory,
            engagement_form: formValues.engagement_form || null,
            work_load_min_hours: formValues.work_load_min_hours ?? null,
            work_load_max_hours: formValues.work_load_max_hours ?? null,
        };
    };

    const buildUpdatePayload = (): Partial<IJobDetails> | null => {
        const jobDemand = buildJobDemand();
        if (!jobDemand) return null;
        return {
            title: jobDemand.title,
            description: jobDemand.description,
            job_type_id: jobDemand.job_type_id,
            work_load_id: jobDemand.work_load_id,
            job_type_label: jobDemand.job_type_label,
            search_location_id: jobDemand.search_location_id,
            search_location_label: jobDemand.search_location_label,
            regions_id: jobDemand.regions_id,
            postal_code: jobDemand.postal_code,
            radius: jobDemand.radius,
            job_category: jobDemand.job_category,
            contactEmail: jobDemand.contactEmail,
            pricing_model: 'per_job',
            engagement_form: jobDemand.engagement_form ?? undefined,
            work_load_min_hours: jobDemand.work_load_min_hours ?? undefined,
            work_load_max_hours: jobDemand.work_load_max_hours ?? undefined,
        };
    };

    const handleSaveDraft = async () => {
        try {
            await form.validateFields(['title', 'location']);
        } catch {
            return;
        }
        if (!selectedCategory) return;

        setIsSavingDraft(true);
        try {
            if (draftId) {
                const payload = buildUpdatePayload();
                if (!payload) return;
                await updateJobDemand(draftId, payload);
                notification.success({
                    message: 'Entwurf gespeichert',
                    description: 'Ihre Änderungen wurden gespeichert.',
                });
            } else {
                const jobDemand = buildJobDemand();
                if (!jobDemand) return;
                const result = await addJobDemand({...jobDemand, save_as_draft: true});
                const newJobId = result?.id ?? result;
                if (newJobId && selectedAddons.length > 0) {
                    sessionStorage.setItem(DRAFT_ADDONS_KEY(newJobId), JSON.stringify(selectedAddons));
                }
                notification.success({
                    message: 'Entwurf gespeichert',
                    description: 'Ihre Stellenanzeige wurde als Entwurf gespeichert.',
                });
            }
            if (draftId && selectedAddons.length > 0) {
                sessionStorage.setItem(DRAFT_ADDONS_KEY(Number(draftId)), JSON.stringify(selectedAddons));
            }
            navigate('/jobs');
        } catch (e) {
            console.error(e);
            notification.error({message: "Es ist ein Fehler beim Speichern aufgetreten."});
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleCheckout = async () => {
        try {
            await form.validateFields(['title', 'location']);
        } catch {
            return;
        }
        if (!selectedCategory) return;
        if (selectedAddons.includes('logo') && !logoUrl) {
            notification.error({message: 'Bitte laden Sie Ihr Firmenlogo hoch, um fortzufahren.'});
            return;
        }

        setIsLoading(true);
        try {
            let jobDemandId: number;
            if (draftId) {
                const payload = buildUpdatePayload();
                if (!payload) return;
                await updateJobDemand(draftId, payload);
                jobDemandId = Number(draftId);
            } else {
                const jobDemand = buildJobDemand();
                if (!jobDemand) return;
                const jobResult = await addJobDemand(jobDemand);
                jobDemandId = jobResult.id || jobResult;
            }

            const checkoutResult = await createJobCheckoutSession({
                job_demand_id: jobDemandId,
                job_category: selectedCategory,
                addons: selectedAddons.length > 0 ? selectedAddons : undefined,
            });

            if (checkoutResult.status === "activated") {
                notification.success({
                    message: 'Stellenanzeige aktiviert',
                    description: 'Ihre kostenlose Stellenanzeige wurde erfolgreich aktiviert.',
                });
                navigate('/jobs');
            } else if (checkoutResult.status === "checkout" && checkoutResult.url) {
                window.location.href = checkoutResult.url;
            }
        } catch (e) {
            console.error(e);
            notification.error({message: "Es ist ein Fehler beim Anlegen der Stellenanzeige aufgetreten."});
        } finally {
            setIsLoading(false);
        }
    };

    const isEditDraft = !!draftId;
    const isLoadingDraft = !!draftId && !draftJob;

    if (isLoadingDraft) {
        return <Loader/>;
    }

    return (
        <div style={{paddingBottom: 64}}>
            <PageTitle title={isEditDraft ? "Stellenanzeige aktivieren" : "Neue Stellenanzeige anlegen"}/>
            <Row>
                <Col span={isMobile ? 24 : 14}>
                    <Space direction="vertical" size="large" style={{width: '100%'}}>
                        <Form form={form} layout="vertical" initialValues={formInitialValues} key={draftId || 'new'}>
                            <Form.Item
                                name="title"
                                label="Titel der Stelle:"
                                rules={[{required: true, message: 'Bitte füllen Sie alle Felder aus.'}]}
                            >
                                <Input size={isMobile ? "large" : "middle"} placeholder="Titel der Stelle"/>
                            </Form.Item>

                            <Form.Item
                                name="location"
                                label="Standort:"
                                rules={[{required: true, message: 'Bitte füllen Sie alle Felder aus.'}]}
                            >
                                <StyledInput
                                    onClick={() => setIsWhereModalOpen(true)}
                                    readOnly
                                    style={{width: '100%'}}
                                    prefix={isMobile ? <SearchOutlined/> : <EditOutlined/>}
                                    placeholder="Postleitzahl (empfohlen) oder Region wählen"
                                />
                            </Form.Item>

                            <Alert style={{marginBottom: 16}}
                                   message={(!whereSelectedValue || whereSelectedValue?.label_client.includes("Region:")) ?
                                       "Wählen Sie soweit möglich immer eine Postleitzahl und legen Sie für jeden Standort eine eigene Stellenanzeige an" :
                                       "Legen Sie für jeden Standort eine eigene Stellenanzeige an um alle Kandidaten zu erreichen"
                                   }
                                   showIcon type="info"/>

                            <Form.Item name="job_type" label="Beschäftigungsart:">
                                <Select size={isMobile ? "large" : "middle"} placeholder="Beschäftigungsart auswählen"
                                        options={jobTypes}/>
                            </Form.Item>

                            <Form.Item name="engagement_form" label="Einsatzform:">
                                <Select size={isMobile ? "large" : "middle"} placeholder="Einsatzform auswählen"
                                        options={ENGAGEMENT_FORM_OPTIONS}
                                        allowClear/>
                            </Form.Item>

                            <Form.Item label="Arbeitspensum pro Woche:">
                                <Space.Compact style={{width: '100%'}}>
                                    <Form.Item name="work_load_min_hours" noStyle>
                                        <Select size={isMobile ? "large" : "middle"} placeholder="von (min. Stunden)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 120}} allowClear/>
                                    </Form.Item>
                                    <Form.Item
                                        name="work_load_max_hours"
                                        noStyle
                                        rules={[
                                            ({getFieldValue}) => ({
                                                validator(_, value) {
                                                    const min = getFieldValue('work_load_min_hours');
                                                    if (value != null && min != null && value < min) {
                                                        return Promise.reject(new Error('Max. muss ≥ Min. sein'));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <Select size={isMobile ? "large" : "middle"} placeholder="bis (max. Stunden)" options={WORK_LOAD_HOURS_OPTIONS} style={{minWidth: 120}} allowClear/>
                                    </Form.Item>
                                </Space.Compact>
                            </Form.Item>

                            <Form.Item name="description" label="Stellenbeschreibung (bitte anpassen):">
                                <ReactQuill theme="snow" value={courseDetails} onChange={(val) => {
                                    setCourseDetails(val);
                                    form.setFieldValue('description', val);
                                }}/>
                            </Form.Item>
                        </Form>

                        {/* Zielgruppe */}
                        <div>
                            <Title level={5} style={{marginBottom: 12}}>Zielgruppe</Title>
                            <Radio.Group
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                style={{width: '100%'}}
                            >
                                <Space direction="vertical" style={{width: '100%'}}>
                                    <CategoryRadio value="fachkraefte" $selected={selectedCategory === 'fachkraefte'}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                                            <Text>Fachkräfte mit Qualifikation (z.B. Buchhalter, MFA, Ingenieur, Elektriker)</Text>
                                            <Text type="secondary" style={{whiteSpace: 'nowrap', marginLeft: 16, color: colors.text}}>299 €</Text>
                                        </div>
                                    </CategoryRadio>
                                    <CategoryRadio value="hilfskraefte" $selected={selectedCategory === 'hilfskraefte'}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                                            <Text>Hilfskräfte (z.B. Aushilfe, Lagerhilfe, Empfangskraft)</Text>
                                            <Text type="secondary" style={{whiteSpace: 'nowrap', marginLeft: 16, color: colors.text}}>kostenlos</Text>
                                        </div>
                                    </CategoryRadio>
                                </Space>
                            </Radio.Group>
                        </div>

                        {/* Add-ons */}
                        <div>
                            <Title level={5} style={{marginBottom: 4}}>Chance auf Bewerber erhöhen: Anzeige hervorheben</Title>
                            <Space direction="vertical" style={{width: '100%', marginTop: 8}} size={8}>
                                {ADDON_DISPLAY.map(addon => (
                                    <AddonRow key={addon.key} $selected={selectedAddons.includes(addon.key)} onClick={() => handleAddonToggle(addon.key)}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                                            <div>
                                                <Checkbox
                                                    checked={selectedAddons.includes(addon.key)}
                                                    tabIndex={-1}
                                                >
                                                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                        <span style={{display: 'flex', color: '#1890ff'}}>{addon.icon}</span>
                                                        <Text strong>{addon.label}</Text>
                                                    </div>
                                                </Checkbox>
                                                <Text type="secondary" style={{marginLeft: 24, display: 'block', color: colors.text}}>
                                                    {addon.description}
                                                </Text>
                                            </div>
                                            <Text type="secondary" style={{whiteSpace: 'nowrap', marginLeft: 16, color: colors.text}}>{addon.price} €</Text>
                                        </div>
                                    </AddonRow>
                                ))}
                            </Space>
                        </div>

                        {/* Logo upload */}
                        {selectedAddons.includes('logo') && (
                            <LogoUploadSection>
                                <Text strong>Firmenlogo</Text>
                                <div style={{display: 'flex', alignItems: 'center', gap: 16, marginTop: 8}}>
                                    <Avatar
                                        size={80}
                                        shape="square"
                                        src={logoUrl}
                                        icon={!logoUrl ? <UserOutlined/> : undefined}
                                        style={{border: '1px solid #d9d9d9'}}
                                    />
                                    <Upload
                                        accept="image/*"
                                        showUploadList={false}
                                        beforeUpload={(file) => {
                                            handleLogoUpload(file);
                                            return false;
                                        }}
                                    >
                                        <Button icon={<CameraOutlined/>} loading={uploadingLogo}>
                                            {logoUrl ? 'Logo ändern' : 'Logo hochladen'}
                                        </Button>
                                    </Upload>
                                </div>
                                {!logoUrl && (
                                    <Text type="danger" style={{display: 'block', marginTop: 8, fontSize: 12}}>
                                        Bitte laden Sie Ihr Firmenlogo hoch, um fortzufahren.
                                    </Text>
                                )}
                            </LogoUploadSection>
                        )}

                        {/* Total price + Action buttons */}
                        {selectedCategory && (
                            <div>
                                <TotalBar>
                                    <Text strong style={{fontSize: 16}}>Gesamtbetrag exkl. MwSt.</Text>
                                    <Title level={3} style={{margin: 0}}>
                                        {totalMonthly === 0 ? 'Kostenlos' : `${totalMonthly} €`}
                                    </Title>
                                </TotalBar>
                                <Text type="secondary" style={{display: 'block', marginTop: 8}}>
                                    Ihre Anzeige verlängert sich automatisch alle 30 Tage, bis Sie Ihr Inserat deaktivieren oder löschen.
                                </Text>
                            </div>
                        )}
                        <BtnWrapper>
                            <Button
                                size="large"
                                onClick={handleSaveDraft}
                                loading={isSavingDraft}
                            >
                                Entwurf speichern
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleCheckout}
                                loading={isLoading}
                                disabled={!selectedCategory || (selectedAddons.includes('logo') && !logoUrl)}
                                icon={<ShoppingCartOutlined/>}
                            >
                                Anzeige schalten
                            </Button>
                        </BtnWrapper>
                    </Space>
                </Col>
                <Col span={isMobile ? 24 : 10} style={{paddingLeft: isMobile ? 0 : 32}}>
                    <FAQ isPrivateUser={false}/>
                </Col>
            </Row>
            <AddressModal
                open={isWhereModalOpen}
                onClose={() => setIsWhereModalOpen(false)}
                onSave={handleSaveWhere}
                initialValue={whereSelectedValue}
            />
        </div>
    );
};

const TotalBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #fafafa;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
`;

const BtnWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 16px;
`;

const CategoryRadio = styled(Radio)<{ $selected: boolean }>`
    display: flex;
    width: 100%;
    padding: 12px 16px;
    border: 1px solid ${props => props.$selected ? '#1890ff' : '#d9d9d9'};
    border-radius: 8px;
    background: ${props => props.$selected ? '#e6f7ff' : '#fff'};
    transition: all 0.2s;

    &:hover {
        border-color: #1890ff;
    }

    .ant-radio {
        margin-top: 2px;
    }

    & > span:last-child {
        flex: 1;
    }
`;

const AddonRow = styled.div<{ $selected: boolean }>`
    padding: 10px 12px;
    border: 1px solid ${props => props.$selected ? '#1890ff' : '#f0f0f0'};
    border-radius: 8px;
    background: ${props => props.$selected ? '#e6f7ff' : '#fafafa'};
    transition: all 0.2s;
    cursor: pointer;

    &:hover {
        border-color: #1890ff;
    }
`;

const LogoUploadSection = styled.div`
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 8px;
    padding: 16px;

    span.ant-avatar.ant-avatar-square.ant-avatar-image img {
        object-fit: contain !important;
    }
`;
